// Card 42, lines of bearing (R548, ui-map-interface.md §17.3.42, §6.1).
//
// The owner ruled that this piece REPLACES the dotted order lines of §6.1.
// A commander already sees where every own fleet is going; what the dotted
// line never said is the course and the time. So this draws the same lane
// the fleet layer drew, keeps its target ring, and writes `BRG 214° · ETA
// 0:41` along it.
//
// **The handover is one flag and it is spent on every pack.** The piece
// sets `FleetLayer.ordersOff` on each frame it draws, and the layer's own
// pack clears it. A chart that switches off, leaves its zoom band or goes
// under a film therefore gives the dotted lines back on the next frame, and
// no path exists by which both sets stand on the water together.
//
// **Own fleets only**, which is what the fleet layer drew as well: a lane
// behind a contact is an order the server withheld (§5).
//
// The colours stay `FLEET_ORDER_COLORS`, so the lane, the pick crosshair of
// §8 and the ring all read the same mission (R469). A move that carries a
// `pledgeSite` is a commit and wears the commit gold (R1208).

import { chart as S } from '../../../gen/strings';
import { Mission } from '../../../gen/proto/aquaphobia';
import {
  bearingDeg, compassPoint, etaSec, formatBearing, formatEta,
} from '../../../render/bearing';
import { toRoman } from '../../../render/roman';
import { runWidth, type GlyphMetrics } from '../../../render/text-run';
import type { FrameInfo } from '../../layer';
import { bandScale } from '../steps';
import {
  ORDER_DASHES, ORDER_DASH_FILL, ORDER_STYLES, ORDER_WIDTH, orderStyleOf,
  type OrderStyle,
} from '../../layers/fleets';
import { CHART_INK, faceOf, hourOfDay, onTheHour } from './shared';
import {
  type ChartContext, type ChartFleet, type ChartSinks, type Piece,
} from '../context';
import type { WordAlongOpts } from '../glyphs';

/**
 * The mission colour and alpha, from the one table the crosshair reads. A
 * pledging move is a commit and wears the commit gold, and the order of
 * a fleet that works a commission wears the commission orchid, as the
 * fleet layer's own lines do (R1208, R2833).
 */
function styleOf(f: ChartFleet): OrderStyle {
  return orderStyleOf(f.mission, f.pledgeSite !== 0, f.commissioned) ?? ORDER_STYLES[0];
}

/** The hostile the hull chases, its focus first, when the chart holds that
 *  hull (R544, R2754). */
function preyOf(f: ChartFleet, byId: ReadonlyMap<number, ChartFleet>): ChartFleet | undefined {
  const id = f.focus !== 0 ? f.focus : f.chase;
  return id === 0 ? undefined : byId.get(id);
}

/** How many forms a label has: the whole, the one with no words, the bearing alone. */
export const BEARING_FORMS = 3;

/**
 * The room for a label on a lane of `lane` world px (R2738): the label is
 * centred `at` along the lane, so it may reach the nearer end and no
 * further, and `share` of that span is the room.
 */
export function labelRoom(lane: number, at: number, share: number): number {
  return 2 * Math.min(at, 1 - at) * lane * share;
}

/**
 * The first of `n` forms, longest first, whose width fits `room` (R2738),
 * or '' where none fits. `k` is the band scale of the text step: the
 * shader grows each glyph and its offset from the pivot by it, so the
 * label covers `width × k` of the water.
 */
export function firstFit(
  n: number, form: (i: number) => string, width: (s: string) => number,
  k: number, room: number,
): string {
  for (let i = 0; i < n; i++) {
    const s = form(i);
    if (width(s) * k <= room) return s;
  }
  return '';
}

export class BearingLinesPiece implements Piece {
  readonly id = 'bearing_lines' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = [] as const;

  /** The two-point path a label is laid along, reused so no frame allocates. */
  private readonly path = [{ x: 0, y: 0 }, { x: 0, y: 0 }];
  /** The hulls by id, for the escort and the focus lane. Reused the same way. */
  private readonly byId = new Map<number, ChartFleet>();
  /** The camera zoom of the frame in hand: the band scale of the label reads it. */
  private zoom = 1;
  /** The advances of the label face, asked once a frame at the first label
   *  and cleared by `emit`, so a lane allocates no metrics object. */
  private metrics: GlyphMetrics | null = null;
  /** The label of the lane in hand, which `formOf` reads: the bearing, the
   *  arrival, the reading and the context. */
  private brg = 0;
  private sec = 0;
  private hist = false;
  private ctx: ChartContext | null = null;
  /** The two callbacks `firstFit` takes, made once, so a lane makes no closure. */
  private readonly formOf = (k: number): string =>
    this.hist ? this.historical(k, this.brg, this.sec, this.ctx!) : this.modern(k, this.brg, this.sec);
  private readonly widthOf = (s: string): number => runWidth(s, this.metrics!);
  /** The options of the label run, rewritten per lane. */
  private readonly along: WordAlongOpts = { anchor: 'centre', startS: 0 };

  rebuild(_ctx: ChartContext): void { /* the orders are read at every pack */ }

  /**
   * Re-arms the handover.
   *
   * It runs before the pack of this frame and after the fleet layer's own,
   * so the layer reads the flag on the NEXT frame. One frame of lag is
   * invisible, and the alternative — the chart reaching into the fleet
   * layer's update order — is not.
   */
  update(ctx: ChartContext, _frame: FrameInfo): void {
    const fleets = ctx.scene.fleets;
    if (fleets) fleets.ordersOff = true;
  }

  emit(sinks: ChartSinks, ctx: ChartContext, frame: FrameInfo): void {
    this.zoom = frame.cam.zoom;
    this.metrics = null;
    const layer = ctx.scene.fleets;
    if (!layer) return;
    const fleets = layer.chartFleets();
    const home = layer.home;
    const byId = this.byId;
    byId.clear();
    for (const f of fleets) byId.set(f.id, f);
    for (const f of fleets) {
      if (!f.own) continue;
      const st = styleOf(f);
      // A course (R2704): the lane from the hull to the leg it sails now.
      // The fleet layer draws the legs and their rings. The patrol arm
      // below would read `px`, `py`, the end of a patrol lane, which a
      // course never sets, and the lane would start at the origin.
      if (f.course) {
        this.lane(sinks, ctx, f, f.x, f.y, f.tx, f.ty, st, 0, 0);
        continue;
      }
      switch (f.mission) {
        case Mission.MISSION_PATROL: {
          // A patrol that chases runs the lane to its prey (R2754), as the
          // fleet layer draws it.
          const t = preyOf(f, byId);
          if (t) this.lane(sinks, ctx, f, f.x, f.y, t.x, t.y, st, 0, 16);
          // The whole lane, both ends ringed, and the bearing of the leg.
          else this.lane(sinks, ctx, f, f.px, f.py, f.tx, f.ty, st, 9, 9);
          break;
        }
        case Mission.MISSION_GATHER:
          this.lane(sinks, ctx, f, f.x, f.y, f.tx, f.ty, st, 0, 11);
          if (home) this.lane(sinks, ctx, f, f.tx, f.ty, home.x, home.y, st, 0, 0, true);
          break;
        case Mission.MISSION_RETURN:
          if (home) this.lane(sinks, ctx, f, f.x, f.y, home.x, home.y, st, 0, 11);
          break;
        case Mission.MISSION_HOME:
          if (home) this.lane(sinks, ctx, f, f.x, f.y, home.x, home.y, st, 0, 9);
          break;
        case Mission.MISSION_ESCORT: {
          const t = byId.get(f.escorting);
          if (t) this.lane(sinks, ctx, f, f.x, f.y, t.x, t.y, st, 0, 16);
          break;
        }
        case Mission.MISSION_ANCHOR:
          // An anchored hull runs nowhere, so it carries a ring and no
          // bearing at all.
          this.ring(sinks, f.x, f.y, 20, st);
          break;
        default: {
          // A named hostile is a focus, so the lane goes to that hull and
          // not to the point the order carries (R544). The prey of an
          // attack-move chase takes the same lane (R2754).
          const t = preyOf(f, byId);
          if (t) this.lane(sinks, ctx, f, f.x, f.y, t.x, t.y, st, 0, 16);
          else this.lane(sinks, ctx, f, f.x, f.y, f.tx, f.ty, st, 0, 7);
        }
      }
    }
  }

  // -- the private half ------------------------------------------------------

  /** One dashed target ring, exactly as `packOrders` drew it. */
  private ring(
    sinks: ChartSinks, x: number, y: number, r: number,
    st: { color: number; alpha: number },
  ): void {
    sinks.circles.dashed(
      x, y, r, ORDER_WIDTH, ORDER_DASHES, ORDER_DASH_FILL, st.color, st.alpha,
    );
  }

  /**
   * One lane: the line, the two rings, and the bearing with the arrival
   * time laid along it.
   *
   * `quiet` is the gather's home leg. The card draws that leg with no
   * figure, because the bearing of a leg the hull has not started on is a
   * course nobody is steering yet.
   */
  private lane(
    sinks: ChartSinks, ctx: ChartContext, f: ChartFleet,
    x0: number, y0: number, x1: number, y1: number,
    st: { color: number; alpha: number },
    r0: number, r1: number, quiet = false,
  ): void {
    const t = ctx.cfg.pieces.bearing_lines;
    const fig = t.fig;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const dx = x1 - x0, dy = y1 - y0;
    const len = Math.hypot(dx, dy);
    if (r0 > 0) this.ring(sinks, x0, y0, r0, st);
    if (r1 > 0) this.ring(sinks, x1, y1, r1, st);
    if (len < fig.stand_off + fig.end_off) return;
    const ux = dx / len, uy = dy / len;
    const ax = x0 + ux * fig.stand_off, ay = y0 + uy * fig.stand_off;
    const bx = x1 - ux * fig.end_off, by = y1 - uy * fig.end_off;
    sinks.segments.line(
      ax, ay, bx, by, fig.width, st.color, fig.alpha, 0, r.dash[0], r.dash[1],
    );
    if (quiet) return;
    // The label rides the lane, lifted along the normal and turned upright
    // where the course points to the left of the screen.
    const lift = fig.label_lift;
    this.path[0].x = ax + uy * lift; this.path[0].y = ay - ux * lift;
    this.path[1].x = bx + uy * lift; this.path[1].y = by - ux * lift;
    const brg = bearingDeg(x0, y0, x1, y1);
    const sec = this.eta(ctx, f, len);
    // The label steps down on a short lane (R2738): the longest form that
    // fits the room, and no label where the shortest form does not fit.
    // A run longer than its path folds its last glyphs onto the end of
    // the path, and the reader sees the label warp.
    const face = faceOf(ctx.reading);
    this.metrics ??= ctx.glyphs.stepMetrics(face, t.text.main);
    const lane = Math.hypot(bx - ax, by - ay);
    this.brg = brg; this.sec = sec; this.hist = hist; this.ctx = ctx;
    const text = firstFit(
      BEARING_FORMS, this.formOf, this.widthOf,
      bandScale(t.text.main, this.zoom),
      labelRoom(lane, fig.label_at, fig.label_room),
    );
    if (text === '') return;
    this.along.startS = lane * fig.label_at;
    ctx.glyphs.wordAlong(
      sinks.quads, text, this.path,
      face, t.text.main,
      hist ? CHART_INK.pale : st.color, r.alpha,
      this.along,
    );
  }

  /** Form `k` of the modern label: 0 the whole, 1 no words, 2 the bearing alone. */
  private modern(k: number, brg: number, sec: number): string {
    const b = formatBearing(brg);
    if (k === 0) return S.bearing_modern({ brg: b, eta: formatEta(sec) });
    if (k === 1) return S.bearing_modern_short({ brg: b, eta: formatEta(sec) });
    return S.bearing_modern_brief({ brg: b });
  }

  /** Form `k` of the historical label: 0 the whole, 1 no hour, 2 the point alone. */
  private historical(k: number, brg: number, sec: number, ctx: ChartContext): string {
    const point = compassPoint(brg, S.compass_points);
    if (k === 0) {
      return S.bearing_historical({
        point, hour: onTheHour(hourOfDay(ctx.worldClockS)), eta: this.words(sec),
      });
    }
    if (k === 1) return S.bearing_historical_short({ point, eta: this.words(sec) });
    return S.bearing_historical_brief({ point });
  }

  /**
   * How long the hull needs, in WORLD seconds (R591).
   *
   * The speed is the class figure of the server's own catalog, in units per
   * hour, which `numbers.md §3` states as px per MAP hour. The rendered
   * speed is not used: it is a smoothed picture of the last second, and a
   * hull that has just turned would state an arrival nobody gets.
   *
   * The travel is therefore a map-clock figure, and the label reads it in
   * world time like every other duration on screen.
   */
  private eta(ctx: ChartContext, f: ChartFleet, len: number): number {
    const uh = ctx.state.ship(f.cls)?.speedUh ?? 0;
    return ctx.state.mapToWorldSec(etaSec(len, uh / 3600));
  }

  /** The arrival in words: Roman minutes, or Roman hours past the hour. */
  private words(sec: number): string {
    if (!Number.isFinite(sec)) return formatEta(sec);
    const minutes = Math.max(1, Math.ceil(sec / 60));
    if (minutes < 60) return S.bearing_eta_minutes_historical({ n: toRoman(minutes) });
    return S.bearing_eta_hours_historical({ n: toRoman(Math.round(minutes / 60)) });
  }
}

export const bearingLines = new BearingLinesPiece();
